/**
 * Summary row for a section: reorder handle, type icon + name, description,
 * read-only Required/AI-allowed checkmarks, and a row actions menu. All
 * fields are edited in the section drawer, opened by clicking the row (or
 * "Edit section" in the menu) — this row never grows an inline-editable cell.
 */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TableCell,
  TableRow,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { CheckIcon, MoreVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DragHandle } from '@/shared/ui';
import { BriefSectionTypeIcon } from '../BriefSectionTypeIcon';
import { SECTION_TYPE_LABEL_KEY, type BriefSectionType } from '../../lib/sectionTypes';

export interface BriefSectionRowProps {
  tempId: string;
  name: string;
  /** Author-facing guidance for this section; `null`/blank renders an empty cell. */
  description: string | null;
  type: BriefSectionType;
  required: boolean;
  aiAllowed: boolean;
  /** Added since the template was last saved; it only exists on the page until then. */
  draft: boolean;
  disabled: boolean;
  onOpen: (tempId: string) => void;
  onDelete: (tempId: string) => void;
}

export function BriefSectionRow(props: BriefSectionRowProps) {
  const { t } = useTranslation('knowledge');

  const sortable = useSortable({ id: props.tempId, disabled: props.disabled });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const displayName =
    props.name ||
    t(
      props.draft
        ? 'knowledgeHub.briefSectionRow.newSection'
        : 'knowledgeHub.briefSectionRow.untitled',
    );
  const typeLabel = t(SECTION_TYPE_LABEL_KEY[props.type]);

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const stopPropagation = (event: MouseEvent) => event.stopPropagation();

  return (
    <TableRow
      ref={setNodeRef}
      style={rowStyle}
      data-testid="brief-template-section-row"
      data-temp-id={props.tempId}
      className={clsx('cursor-pointer', isDragging && 'opacity-50')}
      onClick={() => props.onOpen(props.tempId)}
    >
      <TableCell className="w-12">
        <DragHandle
          ariaLabel={t('knowledgeHub.briefSectionRow.dragSection', { name: displayName })}
          attributes={attributes}
          listeners={listeners}
          disabled={props.disabled}
          onClick={stopPropagation}
        />
      </TableCell>
      <TableCell className="w-40 @3xl:w-60">
        <div className="flex items-center gap-xs">
          <BriefSectionTypeIcon
            type={props.type}
            label={typeLabel}
            className="size-4 shrink-0 text-muted-foreground"
            data-testid="brief-template-section-row-type-icon"
          />
          {props.name ? (
            <span className="truncate">{displayName}</span>
          ) : (
            <span className="truncate text-muted-foreground italic">{displayName}</span>
          )}
          {props.draft && (
            <Badge variant="outline" size="sm" className="shrink-0">
              {t('knowledgeHub.briefSectionRow.notSaved')}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="truncate text-muted-foreground" title={props.description || undefined}>
        {props.description}
      </TableCell>
      <TableCell className="hidden w-29 text-center @3xl:table-cell">
        {props.required && (
          <CheckIcon
            aria-hidden
            data-testid="brief-template-section-row-required-check"
            className="mx-auto size-4"
          />
        )}
      </TableCell>
      <TableCell className="hidden w-29 text-center @3xl:table-cell">
        {props.aiAllowed && (
          <CheckIcon
            aria-hidden
            data-testid="brief-template-section-row-ai-allowed-check"
            className="mx-auto size-4"
          />
        )}
      </TableCell>
      <TableCell className="w-22">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('knowledgeHub.briefSectionRow.sectionActionsNamed', {
                name: displayName,
              })}
              disabled={props.disabled}
              onClick={stopPropagation}
            >
              <MoreVerticalIcon aria-hidden className="size-[16px]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={stopPropagation}>
            <DropdownMenuItem onSelect={() => props.onOpen(props.tempId)}>
              <PencilIcon aria-hidden className="mr-2 size-4" />
              {t('knowledgeHub.briefSectionRow.editSection')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => props.onDelete(props.tempId)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2Icon aria-hidden className="mr-2 size-4" />
              {t('knowledgeHub.briefSectionRow.deleteSection')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
